import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/internal/operators';
import { scan } from 'rxjs/operators';

/**
 * An operator to receive a list of all previous events
 *
 * ## Example
 *
 * ```javascript
 * interval(1000).pipe(
 *   asArray((a, b) => (a % 5) === (b % 5)),
 *   map(a => a.sort((b, c) => b % 5 - c % 5))
 * ).subscribe(s => console.log(s));
 * ```
 * @param {(a: T, b: T) => boolean} duplicateCheck: If true, old values are replaced with this one.
 * @param {(oldDuplicate: T | undefined, obj: T) => boolean} preFilterCheck: If false, the [obj] is discarded.
 * @param {(a: T, b: T) => boolean} filterCheck: If false, this entry is removed.
 * @returns {(source: Observable<T>) => Observable<T[]>}
 */
export const asArray = <T>(
  duplicateCheck: (a: T, b: T) => boolean = () => false,
  filterCheck: (e: T) => boolean = () => true,
  preFilterCheck: (oldDuplicate: T | undefined, obj: T) => boolean = () => true) => (source: Observable<T>) =>
  source.pipe(
    scan<T>((a, v) => {
      const oldDuplicate = a.find(va => duplicateCheck(va, v));

      if (!preFilterCheck(oldDuplicate, v)) {
        // Discard the value and return the original array
        return a;
      } else if (oldDuplicate !== undefined) {
        // Replace the value
        return [...a.filter(va => !duplicateCheck(va, v)), v];
      } else {
        // Append the value
        return [...a, v]
      }
    }, []),
    map(arr => arr.filter(e => filterCheck(e))),
    distinctUntilChanged(),
    debounceTime(50),
  ) as Observable<T[]>;

export function encodeTripId(journeyRef: string, operationDate: string) {
  // ID format: journeyRef@operationDate
  const id = `${journeyRef}@${operationDate}`;
  return encodeURIComponent(id);
}

export function decodeTripId(predictionId: string): string[] {
  const id = decodeURIComponent(predictionId);
  return id.split('@');
}

function checkAndCleanupObject(original: any, copy: any) {
  Object.keys(original)
    .forEach(k => {
      // Throw an error if a function is present.
      if (typeof original[k] === 'function') {
        throw new Error('Copied object contains functions!');
      }

      // Convert Dates back to Date-Objects (JSON.parse creates only strings for Date objects)
      else if (original[k] instanceof Date) {
        copy[k] = new Date(original[k]);
      }

      // null or undefined need no conversion
      else if (original[k] === null || original[k] === undefined) {
        copy[k] = original[k];
      }

      // recursively check and cleanup nested objects
      else if (typeof original[k] === 'object') {
        checkAndCleanupObject(original[k], copy[k]);
      }
    });
}

/// Copies an object without functions. Allowed property types are: number, string, Date + nested objects.
export function copyObject<T extends any>(obj: T): T {
  // We don't copy null or undefined.
  if (obj === null || obj === undefined) {
    return obj;
  }

  const copy = JSON.parse(JSON.stringify(obj)) as T;

  checkAndCleanupObject(obj, copy);

  return copy;
}



// WEBPACK FOOTER //
// ./src/helpers.ts